'use client';


import ImageProxy from '@next-image-proxy';

import { CampaignSlug, IntegrationSlug } from '@api';

import { ExternalLink } from '@uikit/components/ExternalLink';

import useDisableRedirectOnLogin from '@shared/hooks/useDisableRedirectOnLogin';

import { useIntegrationCampaignLeaderboardInfiniteQuery } from '@query/story';

import CustomStoryBanner from '../CustomStoryBanner';
import CustomStoryLayout from '../CustomStoryLayout';
import { CampaignCounter } from './CampaignCounter';
import { FoxsyCampaignLeaderBoard } from './FoxsyCampaignLeaderBoard';
import { GateIoLink } from './GateIoLink';
import StepToJoinFoxsyCampaign from './StepToJoinFoxsyCampaign';
import { TradingVolumeProgress } from './TradingVolumeProgress';

const FoxsyCampaignStory = () => {
  useDisableRedirectOnLogin();

  const leaderboardQuery = useIntegrationCampaignLeaderboardInfiniteQuery(
    IntegrationSlug.GOODCRYPTOX,
    CampaignSlug.FOXSY_AI,
  );

  const competition = leaderboardQuery.data?.pages[0].competition;

  return (
    <CustomStoryLayout
      title="Trade FOXSY & Win Big – 500,000 $FOXSY Up for Grabs!"
      banner={
        <CustomStoryBanner
          alt="500,000 $FOXSY Trading Contest GoodCryptoX and Foxsy.AI"
          desktop={{
            src: '/store/stories/foxsy-ai-1000000-trading-competition/banner.png',
            width: 1742,
            height: 296,
            quality: 100,
          }}
        />
      }
    >
      <div className="story-prose max-w-[1162px]">
        <p>
          <b>FoxsyAI & Magic Square</b> bring you an <b>exclusive FOXSY trading contest!</b>
          Trade{' '}
          <b>
            FOXSY on <GateIoLink />
          </b>{' '}
          via the <b>GoodCryptoX</b> platform and share up to <b>500,000 $FOXSY</b> in rewards!
        </p>

        <h2>Why Trade with GoodCryptoX?</h2>

        <ul className="list-none pl-0">
          <li className="mb-3">✅ Trade on 35+ CEXs with advanced tools</li>
          <li className="mb-3">🤖 Automate your trading with AI-powered bots</li>
          <li className="mb-3">🔥 Non-custodial trading with full control over your funds</li>
        </ul>

        <h2 className="text-base-text-primary">
          💰 Up to 450,000 $FOXSY Prize Pool based on Total Trading Volume!
        </h2>

        <h3 className="text-base-text-primary font-semibold">
          Your rewards depend on the total trading volume from all users combined!
        </h3>

        <ImageProxy
          className="mt-4 md-msq:mt-10"
          src="/store/stories/foxsy-ai-1000000-trading-competition/rewards-table-v2.png"
          width={1163}
          height={386}
          sizes="100vw"
          quality={100}
          alt="Foxsy.AI 1000000 trading competition — Rewards table"
        />

        <h2>⚡ Speed Bonus – 50,000 FOXSY Up for Grabs!</h2>

        <p>
          The first <b>20 traders</b> to reach <b>$1000</b> in total trading volume during the
          contest period will each receive <b>2,500 FOXSY</b> as a special bonus! 🏆
        </p>

        <StepToJoinFoxsyCampaign />

        <h2 className="mt-0">🗓 Event Details</h2>

        <ul className="list-none pl-0">
          <li className="mb-3">
            📅 <b>Event Period:</b> 1st April 2025 (8 AM UTC) - 30th April 2025 (4 PM UTC)
          </li>
          <li className="mb-3">
            🔹 <b>Eligible trades:</b> Only FOXSY/USDT Spot Trading Volume via GoodCryptoX
            API-connected <GateIoLink /> account
          </li>
          <li className="mb-3">
            💰 <b>Minimum Trading Volume to qualify: ≥ $100 USD</b>
          </li>
          <li className="mb-3">
            🎯 <b>Rewards:</b> Based on total trading volume & individual ranking
          </li>
        </ul>

        <h2>📊 Total Trading Volume (All Users Combined)</h2>

        <TradingVolumeProgress
          value={competition?.totalTradingVolumeRegistered}
          maxValue={competition?.tradingVolumeGoal}
        />

        <h2>🏆 Live Leaderboard</h2>

        <FoxsyCampaignLeaderBoard query={leaderboardQuery} />

        <CampaignCounter endDate={competition?.endDate} startDate={competition?.startDate} />

        <h2>📜 Terms & Conditions</h2>

        <ul className="list-none pl-0">
          <li className="font-medium">
            ✔️ Only FOXSY/USDT spot trades on <GateIoLink /> via GoodCryptoX API-connected accounts
            count
          </li>
          <li className="font-medium">
            ✔️ Only trades made during the event period (1st April 2025 - 30th April 2025) are
            eligible
          </li>
          <li className="font-medium">
            ✔️ Users must trade at least $100 in FOXSY/USDT volume to qualify for rewards
          </li>
          <li className="font-medium">
            ✔️ The maximum prize pool is 500,000 FOXSY, distributed based on trading volume
            milestones
          </li>
          <li className="font-medium">
            ✔️ Rewards will be airdropped to your MagicID connected Solana Wallet
          </li>
          <li className="font-medium">
            ✔️ FoxsyAI & Magic Square reserve the right to modify event rules or disqualify
            fraudulent activities
          </li>
          <li className="font-medium">✔️ By participating, users agree to all event terms</li>
        </ul>

        <div className="flex justify-center mb-10">
          <ExternalLink
            href="https://click.goodcrypto.app/b9EC/ie88jiew?ref=wXXJzw"
            className="story-prose-none button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2"
          >
            Start Trading Today
          </ExternalLink>
        </div>
      </div>
    </CustomStoryLayout>
  );
};

export default FoxsyCampaignStory;
